import { PageRoutes } from "./pageRoutes";

type PageChunk = () => Promise<unknown>;

type PrefetchMessage = {
  type: "PREFETCH_URLS";
  urls: string[];
  cache: "public";
};

type PriorityPrefetchMessage = {
  type: "PRIORITY_PREFETCH_URL";
  url: string;
  cache: "public";
};

const PRIORITY_PREFETCH_COOLDOWN_MS = 15000;
const priorityPrefetchMap = new Map<string, number>();
let isPriorityPrefetchBound = false;
const INSIGHTS_PREFIX = `${PageRoutes.insights}/`;

const isInsightsPath = (path: string): boolean => {
  return path === PageRoutes.insights || path.startsWith(INSIGHTS_PREFIX);
};

const PAGE_CHUNKS: Array<{ route: string; importChunk: PageChunk }> = [
  { route: PageRoutes.insights, importChunk: () => import("../pages/insights/insights") },
  { route: `${PageRoutes.insights}/:slug`, importChunk: () => import("../components/Post/BlogPost") },
];

const runInBatches = async <T,>(items: T[], batchSize: number, worker: (item: T) => Promise<unknown>) => {
  for (let index = 0; index < items.length; index += batchSize) {
    const batch = items.slice(index, index + batchSize);
    await Promise.allSettled(batch.map((item) => worker(item)));
  }
};
const getRouteUrls = (): string[] => {
  const routes = [PageRoutes.insights];

  const anchors = Array.from(document.querySelectorAll<HTMLAnchorElement>("a[href]"))
    .map((anchor) => anchor.href)
    .filter((href) => href.startsWith(window.location.origin))
    .map((href) => new URL(href).pathname)
    .filter((pathname) => isInsightsPath(pathname));

  const currentPath = isInsightsPath(window.location.pathname)
    ? [window.location.pathname]
    : [];

  return Array.from(new Set([...routes, ...currentPath, ...anchors])).filter(Boolean);
};

const warmPageChunks = async () => {
  await runInBatches(PAGE_CHUNKS, 3, async (entry) => entry.importChunk());
};

const shouldPrefetch = (): boolean => {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }

  const connection = (navigator as Navigator & {
    connection?: { saveData?: boolean; effectiveType?: string };
  }).connection;

  if (connection?.saveData) return false;
  if (connection?.effectiveType === "slow-2g") return false;
  if (connection?.effectiveType === "2g") return false;

  return "serviceWorker" in navigator;
};

const getActiveWorker = async () => {
  const registration = await navigator.serviceWorker.getRegistration();
  return registration?.active || registration?.waiting || registration?.installing;
};

const postPrefetchUrls = async (urls: string[]) => {
  const worker = await getActiveWorker();

  if (!worker) return;

  const message: PrefetchMessage = {
    type: "PREFETCH_URLS",
    urls,
    cache: "public",
  };

  worker.postMessage(message);
};

const postPriorityPrefetchUrl = async (url: string) => {
  const worker = await getActiveWorker();
  if (!worker) return;

  const message: PriorityPrefetchMessage = {
    type: "PRIORITY_PREFETCH_URL",
    url,
    cache: "public",
  };

  worker.postMessage(message);
};

const bindPriorityLinkPrefetch = () => {
  if (isPriorityPrefetchBound) return;
  isPriorityPrefetchBound = true;

  const queuePriorityPrefetch = (target: EventTarget | null) => {
    if (!(target instanceof Element)) return;

    const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
    if (!anchor) return;

    const rawHref = anchor.getAttribute("href");
    if (!rawHref || rawHref.startsWith("#")) return;

    const url = new URL(anchor.href, window.location.origin);
    if (url.origin !== window.location.origin) return;
    if (!isInsightsPath(url.pathname)) return;

    const cacheKey = `${url.pathname}${url.search}`;
    const now = Date.now();
    const lastSentAt = priorityPrefetchMap.get(cacheKey) ?? 0;

    if (now - lastSentAt < PRIORITY_PREFETCH_COOLDOWN_MS) {
      return;
    }

    priorityPrefetchMap.set(cacheKey, now);
    void postPriorityPrefetchUrl(cacheKey);
  };

  document.addEventListener("mouseover", (event) => queuePriorityPrefetch(event.target), true);
  document.addEventListener("focusin", (event) => queuePriorityPrefetch(event.target), true);
  document.addEventListener("pointerdown", (event) => queuePriorityPrefetch(event.target), true);
};

export const startPrefetchWarmup = (): void => {
  if (!shouldPrefetch()) return;

  bindPriorityLinkPrefetch();

  const runWarmup = () => {
    const urls = getRouteUrls();
    if (!urls.length) return;

    void postPrefetchUrls(urls);
    void warmPageChunks();
  };

  const idleCallback = (window as Window & {
    requestIdleCallback?: (callback: () => void, options?: IdleRequestOptions) => number;
  }).requestIdleCallback;

  if (idleCallback) {
    idleCallback(runWarmup, { timeout: 2500 });
  } else {
    setTimeout(runWarmup, 1500);
  }
};
